import { Box, Flex, IconButton, Text } from "@chakra-ui/react";
import { FaMapMarkerAlt, FaPhoneAlt, FaRegCalendarAlt, FaTrash } from "react-icons/fa";
import { Lead } from "../../../models/Lead";
import { crm, CrmAvatar, Pill, ResponsibleChip } from "./crmTheme";

const LeadCard = ({
  lead,
  onDragStart,
  onClick,
  onDelete,
}: {
  lead: Lead;
  onDragStart: (e: React.DragEvent, lead: Lead) => void;
  onClick: (lead: Lead) => void;
  onDelete: (lead: Lead) => void;
}) => {
  const nextVisit = lead.visits
    ?.slice()
    ?.sort(
      (a, b) =>
        new Date(a.scheduledDate).getTime() -
        new Date(b.scheduledDate).getTime()
    )
    ?.find((v) => new Date(v.scheduledDate).getTime() >= Date.now() - 86400000);

  return (
    <Box
      role="group"
      draggable
      onDragStart={(e) => onDragStart(e, lead)}
      onClick={() => onClick(lead)}
      bg={crm.surface}
      borderRadius="10px"
      border={`1px solid ${crm.border}`}
      boxShadow={crm.shadowSm}
      p="12px"
      cursor="grab"
      _hover={{
        boxShadow: crm.shadowMd,
        transform: "translateY(-1px)",
        borderColor: crm.brand,
      }}
      _active={{ cursor: "grabbing" }}
      transition="all .12s ease"
    >
      <Flex justifyContent="space-between" alignItems="flex-start" gap="8px">
        <Text fontWeight="600" noOfLines={2} color={crm.text} fontSize="sm">
          {lead.name || "Sem nome"}
        </Text>
        <Flex align="center" gap="4px" flexShrink={0}>
          {lead.number != null && (
            <Pill color={crm.textFaint}>#{lead.number}</Pill>
          )}
          <IconButton
            aria-label="Excluir lead"
            icon={<FaTrash />}
            size="xs"
            variant="ghost"
            colorScheme="red"
            opacity={0}
            _groupHover={{ opacity: 1 }}
            transition="opacity .12s"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(lead);
            }}
          />
        </Flex>
      </Flex>

      {(lead.city || lead.state) && (
        <Flex align="center" gap="6px" mt="6px" color={crm.textMuted}>
          <FaMapMarkerAlt size={11} />
          <Text fontSize="sm" noOfLines={1}>
            {[lead.city, lead.state].filter(Boolean).join(" — ")}
          </Text>
        </Flex>
      )}

      {lead.phone && (
        <Flex align="center" gap="6px" mt="4px" color={crm.textMuted}>
          <FaPhoneAlt size={10} />
          <Text fontSize="sm">{lead.phone}</Text>
        </Flex>
      )}

      <Flex mt="10px" gap="6px" flexWrap="wrap" alignItems="center">
        {lead.responsibleName && (
          <ResponsibleChip name={lead.responsibleName} />
        )}
        {nextVisit && (
          <Pill bg={crm.brandSoft} color={crm.brand}>
            <FaRegCalendarAlt size={9} />
            Visita{" "}
            {new Date(nextVisit.scheduledDate).toLocaleDateString("pt-BR")}
          </Pill>
        )}
        {lead.comments && lead.comments.length > 0 && (
          <Pill>💬 {lead.comments.length}</Pill>
        )}
      </Flex>
    </Box>
  );
};

export default LeadCard;
